import { IAttribute } from '../components/DTStudio/interfaces'
import { AttributeType } from '../constants/constants'

export const buildAttributeKey = (
    attributeId: string,
    entityTemplateId: string,
): string => `${attributeId} (${entityTemplateId})`

/**
 * Extracts the entity template id from a key produced by buildAttributeKey
 * (`${attributeId} (${entityTemplateId})`) — the inverse of buildAttributeKey. Returns an
 * empty string if the key is not in the expected format.
 */
export const parseTemplateIdFromKey = (key: string): string => {
    const match = key.match(/\(([^)]*)\)\s*$/)
    return match ? match[1] : ''
}

/**
 * Returns the attributes that are BOTH pre-selected in the application settings (their
 * `${attributeId} (${entityTemplateId})` key is present in preSelectedAttributeKeys) AND
 * chartable-and-not-hidden. `entityTemplateId` must be the template the settings keyed the
 * attributes against — for a relation that is the relation's child-entity template id. This
 * can differ from a related instance's own templateId: a relation is configured against a
 * parent template (e.g. "Device"), but the related instances may be of its sub-templates, so
 * an instance's templateId is a sub-template id rather than the parent the settings used.
 * (UBQ-5476)
 */
export const getPreSelectedChartableAttributes = (
    attributes: IAttribute[],
    entityTemplateId: string,
    preSelectedAttributeKeys: string[],
): IAttribute[] =>
    attributes.filter(
        (attribute) =>
            preSelectedAttributeKeys.includes(
                buildAttributeKey(attribute.id, entityTemplateId),
            ) && isChartableAndNotHidden(attribute),
    )

export const getAttributeById = (
    attributes: IAttribute[] | undefined,
    id: string,
): IAttribute | undefined => {
    return attributes?.find((attribute) => attribute.id === id)
}

export const getAttributeByName = (
    attributes: IAttribute[],
    name: string,
): IAttribute | undefined => {
    return attributes.find((attribute) => attribute.name === name)
}

export const isChartableOrMappableOrTimeSeriesAndNotHidden = (
    item: IAttribute | undefined,
): boolean => {
    return !!(
        (item?.metadata?.TimeSeries ||
            item?.metadata?.Chartable ||
            item?.metadata?.Mappable) &&
        !item?.metadata?.Hidden
    )
}

export const isChartableAndNotHidden = (
    item: IAttribute | undefined,
): boolean => {
    return !!item?.metadata?.Chartable && !item?.metadata?.Hidden
}

export const isChartable = (item: IAttribute | undefined): boolean => {
    return !!item?.metadata?.Chartable
}

export const isMappable = (item: IAttribute | undefined): boolean => {
    return !!item?.metadata?.Mappable && (isGeoFence(item) || isGPS(item))
}

export const isMappableTimeSeriesAndNotHidden = (
    item: IAttribute | undefined,
): boolean => {
    return !!(
        item?.metadata?.Mappable &&
        item?.metadata?.TimeSeries &&
        !item?.metadata?.Hidden
    )
}

/**
 *
 * @param item attribute
 * @returns This method checks if an attribute of type Entity is mappable or not
 */
export const isMappableEntityAttribute = (
    item: IAttribute | undefined,
): boolean => {
    return !!item?.metadata?.Mappable && isEntity(item)
}

export const isDynamic = (item: IAttribute | undefined): boolean => {
    return item?.attributeType === 'Dynamic'
}

export const isTimeSeries = (item: IAttribute | undefined): boolean => {
    return !!item?.metadata?.TimeSeries
}

export const isGeoFence = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.GEO_FENCING
}

export const isGPS = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.GPS
}

export const isDate = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.DATE
}

export const isDateTime = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.DATE_TIME
}

export const isURL = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.URL
}

export const isEntity = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.ENTITY
}

export const isJson = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.JSON
}
export const isRadio = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.RADIO
}

export const isStringType = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.STRING
}

export const isNumberType = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.NUMBER
}

export const isDecimalType = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.DECIMAL
}

export const isSelect = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.SELECT
}

export const isMultiSelect = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.MULTI_SELECT
}

export const isUser = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.USER
}

export const isUserGroup = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.USER_GROUP
}

export const isHidden = (item: IAttribute | undefined): boolean => {
    return !!item?.metadata?.Hidden
}

export const isNotHidden = (item: IAttribute | undefined): boolean => {
    return !isHidden(item)
}

export const isPDFBase64 = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.PDFBASE64
}
export const isEmail = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.EMAIL
}

export const isCurrency = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.CURRENCY
}

export const isTemperature = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.TEMPERATURE
}

export const isRelativeHumidity = (item: IAttribute | undefined): boolean => {
    return (
        item?.dataType?.toLocaleLowerCase() === AttributeType.RELATIVE_HUMIDITY
    )
}

export const isWeight = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.WEIGHT
}

export const isDistance = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.DISTANCE
}

export const isFile = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.FILE
}

export const isBoolean = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.BOOLEAN
}

export const isEntityJson = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.Entity_JSON
}

export const isHumidity = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.HUMIDITY
}

export const isCustomAttribute = (item: IAttribute | undefined): boolean => {
    return (
        item?.dataType?.toLocaleLowerCase() === AttributeType.CUSTOM_ATTRIBUTE
    )
}

export const isCountryISOcode = (item: IAttribute | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.COUNTRYISOCODE
}

export const isAlaramSatus = (item: IAttribute | undefined): boolean => {
    return item?.id === 'alarmStatus'
}

export const isEmptyValue = (
    value: string | unknown[] | Record<string, unknown> | null | undefined,
): boolean => {
    if (value == null) return true
    if (typeof value === 'string') return value.trim().length === 0
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'object' && !Array.isArray(value))
        return Object.keys(value).length === 0
    return false
}
